<script lang="ts">import FieldLabel from '../FieldLabel/FieldLabel.svelte';
import ValidationMessage from '../ValidationMessage/ValidationMessage.svelte';
export let colorMode = 'light';
export let label = '';
export let name;
export let disabled = false;
export let invalid = false;
export let items;
export let readonly = false;
export let required = false;
export let validationMessage = '';
export let selectedValue = '';
function handleClick(event) {
    if (readonly) {
        event.preventDefault();
    }
}
// Due to binding behavior, this instantiation must be done separate from declaration for default value to work properly
selectedValue = items.find((item) => item.selected)?.value ?? '';
</script>

<div {...$$restProps} class="RadioButtonsField {$$restProps.class || ''}" data-color-mode={colorMode}>
  <fieldset
    {disabled}
    role="radiogroup"
    aria-labelledby="radiogroup-label-{name}"
    aria-describedby={invalid ? `radiogroup-error-${name}` : undefined}
    {...invalid ? { 'aria-invalid': 'true' } : {}}
  >
    {#if label}
      <FieldLabel as="legend" id="radiogroup-label-{name}" {disabled} text={label} />
    {/if}

    {#each items as { label, value }}
      <div class="RadioButtonsField__row">
        <label>
          <input type="radio" {name} {value} {required} bind:group={selectedValue} on:click={handleClick} />
          {label}
        </label>
      </div>
    {/each}
  </fieldset>

  {#if validationMessage && invalid}
    <ValidationMessage id="radiogroup-error-{name}" text={validationMessage} {colorMode} />
  {/if}
</div>

<style>.RadioButtonsField label {
  font-weight: var(--form-field-input-font-weight);
  font-size: var(--form-field-input-font-size);
  letter-spacing: var(--form-field-input-letter-spacing);
  line-height: var(--form-field-input-line-height);
}

/**
 * Checkbox styling is shared between CheckboxesField and MultiSelectField
 * The biggest difference between the two is the hover/focus/etc states are on different elements, so it cannot just be
 * one big mixin and has to be broken up
 */
.RadioButtonsField {
  --color: var(--color-text);
}
.RadioButtonsField__row:not(:last-child) {
  margin-bottom: var(--space-10);
}
.RadioButtonsField fieldset[aria-invalid=true] label:has(input[type=radio]:checked):before {
  background-color: var(--color-critical);
}
.RadioButtonsField fieldset[aria-invalid=true] label:before, .RadioButtonsField fieldset[aria-invalid=true] label:hover:before, .RadioButtonsField fieldset[aria-invalid=true] label:has(input[type=radio]:active):before, .RadioButtonsField fieldset[aria-invalid=true] label:has(input[type=radio]:focus):before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-critical);
}
.RadioButtonsField fieldset[aria-invalid=true] label:has(input[type=radio]:focus-visible):before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-critical), 0 0 0 var(--accessibility-focus-outline-offset) var(--color-background), 0 0 0 calc(var(--accessibility-focus-outline-offset) + var(--accessibility-focus-outline-width)) var(--accessibility-focus-outline-color);
}
.RadioButtonsField label {
  position: relative;
  display: inline-flex;
  width: fit-content;
  padding-left: calc(var(--radio-buttons-field-outer-size) + var(--space-10));
  cursor: pointer;
}
.RadioButtonsField label:before, .RadioButtonsField label:after {
  content: "";
  position: absolute;
  border-radius: 100%;
}
.RadioButtonsField label:before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-default);
  top: 0;
  left: 0;
  height: var(--radio-buttons-field-outer-size);
  width: var(--radio-buttons-field-outer-size);
}
.RadioButtonsField label:after {
  top: calc((var(--radio-buttons-field-outer-size) - var(--radio-buttons-field-inner-size)) / 2);
  left: calc((var(--radio-buttons-field-outer-size) - var(--radio-buttons-field-inner-size)) / 2);
  height: var(--radio-buttons-field-inner-size);
  width: var(--radio-buttons-field-inner-size);
  background-color: var(--color);
  opacity: 0;
  filter: invert(100%);
}
.RadioButtonsField label:hover:before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-hover);
}
.RadioButtonsField label:has(input[type=radio]:checked):before {
  background-color: var(--color-form-field-selected);
  box-shadow: none;
}
.RadioButtonsField label:has(input[type=radio]:checked):after {
  opacity: 1;
}
.RadioButtonsField label:has(input[type=radio]:focus):before, .RadioButtonsField label:has(input[type=radio]:checked:focus):before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-focus);
}
.RadioButtonsField label:has(input[type=radio]:checked:focus-visible):before, .RadioButtonsField label:has(input[type=radio]:focus-visible):before {
  --accessibility-focus-outline-offset: 2px;
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-focus), 0 0 0 var(--accessibility-focus-outline-offset) var(--color-background), 0 0 0 calc(var(--accessibility-focus-outline-offset) + var(--accessibility-focus-outline-width)) var(--accessibility-focus-outline-color);
}
.RadioButtonsField label:has(input[type=radio]:active):before, .RadioButtonsField label:has(input[type=radio]:checked:active):before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-active);
}
.RadioButtonsField label:has(input[type=radio]:checked:active):before {
  background-color: var(--color-form-field-active);
}
.RadioButtonsField label:has(input[type=radio]:disabled) {
  pointer-events: none;
}
.RadioButtonsField label:has(input[type=radio]:disabled:checked):before {
  background-color: var(--color-form-field-disabled-box-shadow);
}
.RadioButtonsField label:has(input[type=radio]:disabled:not(:checked)):before {
  box-shadow: inset 0 0 0 var(--radio-buttons-field-outer-width) var(--color-form-field-disabled-box-shadow);
  background-color: var(--color-form-field-disabled-background-color);
}</style>
